import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Space,
  Table,
  Popconfirm,
  message,
  Skeleton,
  Spin,
  Popover,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import {
  deleteDraftCost,
  deleteDraftInvoice,
  getDrafts,
} from 'services/invoices';
import { queryClient } from 'config/query-client';
import { toastError } from 'services/helpers/getError';
import { DraftsDataType } from 'types';
import { INVOICE_DRAFTS } from 'events/types';
import { EditDeleteIcon, LoadingIndicator, TableWrapper } from 'styled';

import useTranslation from '../../customHooks/translate';

const Drafts = () => {
  const translation = useTranslation();

  const [pageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const [draftsData, setDraftsData] = useState<DraftsDataType[]>([]);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Refs for scroll container and loading states
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef(false);

  // Function to fetch drafts data
  const fetchDraftsData = useCallback(
    async (currentPage: number, isNewRequest = false) => {
      if (isFetchingRef.current) return null;

      isFetchingRef.current = true;
      setIsLoadingMore(true);

      try {
        const response = await getDrafts(pageSize, currentPage);
        const data = response.data;

        // Ensure we have data to work with
        if (data?.data?.length) {
          const formattedData = data.data.map((item: any) => ({
            ...item,
            key: item.id,
          }));

          // Set data based on whether this is a new request or not
          if (isNewRequest) {
            setDraftsData(formattedData);
          } else {
            setDraftsData((prev) => [...prev, ...formattedData]);
          }

          // Check if we have more data to load
          const newTotalItems =
            (isNewRequest ? 0 : draftsData.length) + formattedData.length;
          setHasMoreData(newTotalItems < data.totalRecord);

          return { data: formattedData, totalRecord: data.totalRecord };
        }

        setHasMoreData(false);
        return null;
      } catch {
        return null;
      } finally {
        setIsLoadingMore(false);
        isFetchingRef.current = false;
      }
    },
    [pageSize, draftsData.length]
  );

  // Initial data fetch with React Query
  const { status, isLoading } = useQuery({
    queryKey: [INVOICE_DRAFTS, pageSize, pageIndex],
    queryFn: () => fetchDraftsData(pageIndex, pageIndex === 0),
    keepPreviousData: true,
    refetchOnMount: 'always',
  });

  // Reset data function
  const resetData = () => {
    setDraftsData([]);
    setPageIndex(0);
    setHasMoreData(true);
  };

  // Refetch data
  const refetch = () => {
    resetData();
    fetchDraftsData(0, true);
  };

  const handleDelete = (
    id: number | null,
    invoiceNumber: string,
    bankName: string | null
  ) => {
    if (id === null) return;

    const deleteFunction =
      bankName === null ? deleteDraftCost : deleteDraftInvoice;

    deleteFunction(id, invoiceNumber)
      .then(() => {
        message.success(`${translation.messages.successDeleted}`);
        refetch();
        queryClient.invalidateQueries({ queryKey: ['invoiceDrafts'] });
      })
      .catch((error) => {
        toastError(error);
      });
  };

  // Handle table scroll
  const handleTableScroll = useCallback(() => {
    if (
      !tableContainerRef.current ||
      !loadingRef.current ||
      isFetchingRef.current ||
      !hasMoreData
    )
      return;

    const containerRect = tableContainerRef.current.getBoundingClientRect();
    const loadingRect = loadingRef.current.getBoundingClientRect();

    // Check if the loading element is visible
    if (loadingRect.top <= containerRect.bottom + 100) {
      const nextPage = pageIndex + 1;
      setPageIndex(nextPage);
      fetchDraftsData(nextPage);
    }
  }, [pageIndex, hasMoreData, fetchDraftsData]);

  // Set up scroll event listener
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener('scroll', handleTableScroll);
      return () => {
        tableContainer.removeEventListener('scroll', handleTableScroll);
      };
    }
  }, [handleTableScroll]);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const columns: ColumnsType<DraftsDataType> = [
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.drafts.Table_Name}
        </span>
      ),
      dataIndex: 'partnerName',
      key: 'partnerName',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.drafts.Table_Type}
        </span>
      ),
      dataIndex: 'documentType',
      key: 'documentType',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.drafts.Table_JobNumb}
        </span>
      ),
      dataIndex: 'jobNumber',
      key: 'jobNumber',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.drafts.Table_BankAcct}
        </span>
      ),
      dataIndex: 'bankName',
      key: 'bankName',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.drafts.Table_Manager}
        </span>
      ),
      dataIndex: 'manager',
      key: 'manager',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.drafts.Table_Currency}
        </span>
      ),
      dataIndex: 'currency',
      key: 'currency',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.drafts.Table_ExcRate}
        </span>
      ),
      dataIndex: 'exchangeRate',
      key: 'exchangeRate',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.drafts.Table_ContNum}
        </span>
      ),
      dataIndex: 'contractNumber',
      key: 'contractNumber',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.action.Action}
        </span>
      ),
      dataIndex: 'actions',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) =>
        isMobile ? (
          <Popover
            placement="bottom"
            trigger="click"
            content={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Link
                  to={
                    record.bankName === null
                      ? `/cost/new/${record.invoiceId}`
                      : `/invoices/new/${record.invoiceId}`
                  }
                  state={{ record, from: 'debitor', base: 'drafts' }}
                >
                  <div style={{ cursor: 'pointer' }}>
                    <EditDeleteIcon src="assets/edit-icon.svg" alt="Edit" />
                  </div>
                </Link>
                <Popconfirm
                  title={translation.DeleteItem}
                  onConfirm={() =>
                    handleDelete(
                      record.invoiceId,
                      record.invoiceNumber,
                      record.bankName
                    )
                  }
                  okText="Yes"
                  placement="left"
                  cancelText="No"
                >
                  <EditDeleteIcon src="assets/delete-icon.svg" alt="Delete" />
                </Popconfirm>
              </div>
            }
          >
            <MoreVertIcon
              style={{
                fontSize: '29px',
                border: '1px solid #292d32bf',
                borderRadius: '50%',
                padding: '4px',
              }}
            />
          </Popover>
        ) : (
          <Space size="middle">
            <Link
              to={
                record.bankName === null
                  ? `/cost/new/${record.invoiceId}`
                  : `/invoices/new/${record.invoiceId}`
              }
              state={{ record, from: 'debitor', base: 'drafts' }}
            >
              <div style={{ cursor: 'pointer' }}>
                <img className="edit" src="assets/edit-icon.svg" alt="" />
              </div>
            </Link>
            <Popconfirm
              title={translation.DeleteItem}
              onConfirm={() =>
                handleDelete(
                  record.invoiceId,
                  record.invoiceNumber,
                  record.bankName
                )
              }
              okText={<span style={{ color: 'black' }}>Yes</span>}
              cancelText={<span style={{ color: 'black' }}>No</span>}
            >
              <div style={{ cursor: 'pointer' }}>
                <img className="delete" src="assets/delete-icon.svg" alt="" />
              </div>
            </Popconfirm>
          </Space>
        ),
    },
  ];

  return (
    <>
      {status === 'loading' && draftsData.length === 0 ? (
        <Skeleton active />
      ) : (
        <TableWrapper ref={tableContainerRef}>
          <Table
            sticky={{
              offsetHeader: 0,
            }}
            scroll={{ x: 'max-content' }}
            dataSource={draftsData}
            columns={columns}
            loading={isLoading && draftsData.length === 0}
            pagination={false}
          />

          <LoadingIndicator ref={loadingRef}>
            {isLoadingMore && <Spin size="default" />}
            {!hasMoreData && draftsData.length > 0 && (
              <div style={{ color: '#999', marginTop: '8px' }}>
                {translation.messages.allData}
              </div>
            )}
          </LoadingIndicator>
        </TableWrapper>
      )}
    </>
  );
};

export default Drafts;
